import { useState, useEffect, useCallback } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { Badge } from "~/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { ConfirmDestructive } from "~/components/confirm-destructive";
import { Plus, Trash2, Loader2, Globe } from "lucide-react";
import { toast } from "sonner";

interface Webhook {
  id: string;
  table_name: string;
  operations: string[];
  url: string;
  enabled: boolean;
  created_at: string;
}

interface Delivery {
  id: string;
  event: string;
  status_code: number;
  attempts: number;
  created_at: string;
}

export function EventWebhooksPanel({
  open,
  onOpenChange,
  table,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: string | null;
}) {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [_deliveries, _setDeliveries] = useState<Delivery[]>([]);
  const [_selectedWebhook, _setSelectedWebhook] = useState<Webhook | null>(null);
  const [webhookToDelete, setWebhookToDelete] = useState<Webhook | null>(null);

  const fetchWebhooks = useCallback(async () => {
    if (!table) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/event-webhooks?table=${encodeURIComponent(table)}`);
      const data = await res.json();
      setWebhooks(data.webhooks || []);
    } catch { /* fetch failed, leave list empty */ }
    setLoading(false);
  }, [table]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open && table) fetchWebhooks();
  }, [open, table, fetchWebhooks]);

  async function handleToggle(webhook: Webhook) {
    await fetch("/api/event-webhooks", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: webhook.id, enabled: !webhook.enabled }),
    });
    fetchWebhooks();
  }

  async function handleDelete(webhook: Webhook) {
    await fetch("/api/event-webhooks", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: webhook.id }),
    });
    fetchWebhooks();
    toast.success("Webhook deleted");
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[480px] sm:w-[540px] p-0 flex flex-col">
        <SheetHeader className="px-4 pt-4 pb-3 border-b border-border/30 shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-sm">Webhooks: {table}</SheetTitle>
            <Button size="sm" className="text-xs gap-1" onClick={() => setShowCreate(true)}>
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="mx-auto h-8 w-8 text-muted-foreground/30" />
              <p className="mt-3 text-[13px] text-muted-foreground">No webhooks for this table.</p>
            </div>
          ) : (
            webhooks.map((w) => (
              <div key={w.id} className="rounded-md border border-border/30 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Switch checked={w.enabled} onCheckedChange={() => handleToggle(w)} className="scale-75" />
                    <code className="text-xs font-mono text-foreground truncate">{w.url}</code>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-destructive"
                    aria-label={`Delete webhook for ${w.url}`}
                    onClick={() => setWebhookToDelete(w)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-1.5">
                  {w.operations.map((op) => (
                    <Badge key={op} variant="outline" className="text-[10px]">{op}</Badge>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <CreateWebhookDialog
          open={showCreate}
          onOpenChange={setShowCreate}
          table={table || ""}
          onCreated={() => { setShowCreate(false); fetchWebhooks(); }}
        />

        <ConfirmDestructive
          open={webhookToDelete !== null}
          onOpenChange={(next) => { if (!next) setWebhookToDelete(null); }}
          title="Delete webhook"
          description={`Delete webhook for ${webhookToDelete?.url}?`}
          confirmLabel="Delete webhook"
          onConfirm={async () => {
            if (!webhookToDelete) return;
            await handleDelete(webhookToDelete);
            setWebhookToDelete(null);
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

function CreateWebhookDialog({
  open,
  onOpenChange,
  table,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: string;
  onCreated: () => void;
}) {
  const [url, setUrl] = useState("");
  const [ops, setOps] = useState<Set<string>>(new Set(["INSERT", "UPDATE", "DELETE"]));
  const [saving, setSaving] = useState(false);

  function toggleOp(op: string) {
    setOps((prev) => {
      const next = new Set(prev);
      if (next.has(op)) next.delete(op);
      else next.add(op);
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim() || ops.size === 0) return;
    setSaving(true);
    try {
      const res = await fetch("/api/event-webhooks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ table_name: table, operations: Array.from(ops), url: url.trim() }),
      });
      if (res.ok) {
        toast.success("Webhook created");
        setUrl("");
        onCreated();
      } else {
        const data = await res.json();
        toast.error(data.error || "Failed");
      }
    } catch { toast.error("Failed"); }
    setSaving(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New Webhook on {table}</DialogTitle>
            <DialogDescription className="text-[13px]">
              Fire an HTTP POST to your URL when rows are inserted, updated, or deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label className="text-[13px]">URL</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.example.com/webhook" className="text-[13px] font-mono" />
            </div>
            <div className="space-y-1">
              <Label className="text-[13px]">Operations</Label>
              <div className="flex gap-2">
                {["INSERT", "UPDATE", "DELETE"].map((op) => (
                  <button
                    key={op}
                    type="button"
                    onClick={() => toggleOp(op)}
                    className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                      ops.has(op)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-transparent text-muted-foreground border-border hover:border-foreground/30"
                    }`}
                  >
                    {op}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !url.trim() || ops.size === 0}>
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
